import { useState, useCallback, useEffect } from 'react';

// PlanId values match the DB `user_profile.plan_tier` check constraint
// ('free' | 'starter' | 'pro' | 'business'). 'free' is the default state on
// signup and is NOT a purchasable plan — paid tiers below.
export type PlanId = 'starter' | 'pro' | 'business';

export type BillingInterval = 'monthly' | 'yearly';

export interface Plan {
  id: PlanId;
  name: string;
  price: number;             // USD per month (monthly billing)
  priceYearly: number;       // USD per YEAR (yearly billing — cheaper rate)
  period: string;
  description: string;
  features: string[];
  creditsPerMonth: number;   // monthly credit allowance (SAME on both intervals —
                             // yearly = cheaper price, credits still refill monthly)
  isHero?: boolean;          // highlight as the recommended option
}

// Yearly prices below MUST match the amounts on the corresponding yearly
// Stripe Prices (STRIPE_PRICE_*_YEARLY on the backend). Convention here is
// "2 months free" (monthly × 10). If you set different yearly amounts in
// Stripe, update these display values to match — the actual charge always
// comes from Stripe, this is only what the UI shows.
export const PLANS: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 19,
    priceYearly: 190,
    period: 'month',
    description: 'For solo creators getting started',
    creditsPerMonth: 100,
    features: [
      '100 credits per month',
      '1 brand',
      'Regular quality',
      'Full template library',
      'Product, Model & Social posts + free-text',
      'Email support',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 49,
    priceYearly: 490,
    period: 'month',
    description: 'For serious content creators',
    creditsPerMonth: 300,
    isHero: true,
    features: [
      '300 credits per month',
      'Everything in Starter',
      '3 brands',
      'Enhanced & Studio quality',
      'Carousel posts (4 frames)',
      'Ad Strategist + Ad Pilot',
      'Priority generation queue',
    ],
  },
  {
    id: 'business',
    name: 'Business',
    price: 129,
    priceYearly: 1290,
    period: 'month',
    description: 'For agencies and teams',
    creditsPerMonth: 1000,
    features: [
      '1,000 credits per month',
      'Everything in Pro',
      '10 brands',
      'More Ad Strategist analyses + Ad Pilot campaigns',
      'Team seats (coming soon)',
      'Priority support',
    ],
  },
];

// Percentage saved by paying yearly vs 12× monthly, rounded. e.g. Pro:
// (49*12 - 490) / (49*12) = 16.7% → 17. Used for the "Save N%" badge.
export function yearlySavingsPct(plan: Plan): number {
  const monthlyAnnual = plan.price * 12;
  if (monthlyAnnual <= 0) return 0;
  return Math.round(((monthlyAnnual - plan.priceYearly) / monthlyAnnual) * 100);
}

// Pay-as-you-go top-up — buy a one-off bucket of credits without committing
// to a subscription. Surfaced on the checkout page below the plan tiles.
export interface TopUp {
  id: string;
  credits: number;
  price: number;  // USD
  description: string;
}

export const TOP_UPS: TopUp[] = [
  {
    id: 'topup-50',
    credits: 50,
    price: 10,
    description: 'One-off credit pack — never expires',
  },
];

// NOTE — this hook is a transitional stub from before the credit system
// landed. The REAL plan tier + credit balance now lives in Supabase and is
// read via `useCredits`. We keep this hook only to drive the demo checkout
// flow (Stripe is not wired yet — clicking Subscribe shows a stub state).
// Once Stripe webhooks are in, this whole file can be deleted.

const STORAGE_KEY = 'pf_demo_plan';

export function useSubscription() {
  const [currentPlanId, setCurrentPlanId] = useState<PlanId | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? (stored as PlanId) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (currentPlanId) localStorage.setItem(STORAGE_KEY, currentPlanId);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, [currentPlanId]);

  const currentPlan = PLANS.find((p) => p.id === currentPlanId) || null;

  const upgradePlan = useCallback((planId: PlanId) => {
    setCurrentPlanId(planId);
  }, []);

  return {
    currentPlan,
    currentPlanId,
    upgradePlan,
  };
}